import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setError('Invalid reset link. Please request a new one.');
    }
  }, [token, email]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate('/login'), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { email, token, newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D2C54 0%, #1A5490 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <img
                src="/img/acs-nav-logo.png"
                alt="ACS"
                style={{ width: 80 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
              ACS Command Center
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Smarter By Design
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
            Set a New Password
          </Typography>

          {success ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password reset! Redirecting to login...
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {(!token || !email) ? (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{ mt: 2 }}
                >
                  Back to Sign In
                </Button>
              ) : (
                <Box component="form" onSubmit={handleSubmit} autoComplete="on">
                  {/* Hidden username field gives password managers (especially
                      iCloud Keychain) the context they need to update the
                      saved credential for the right account. Without it, Safari
                      keeps offering the OLD password on the next login and the
                      user gets stuck in a reset loop. */}
                  <input
                    type="email"
                    name="username"
                    id="reset-username"
                    value={email}
                    autoComplete="username"
                    readOnly
                    style={{ display: 'none' }}
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    margin="normal"
                    required
                    autoFocus
                    name="new-password"
                    id="reset-new-password"
                    inputProps={{
                      minLength: 6,
                      autoComplete: 'new-password',
                      autoCapitalize: 'none',
                      autoCorrect: 'off',
                      spellCheck: false,
                    }}
                    helperText="At least 6 characters"
                  />
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    margin="normal"
                    required
                    name="confirm-password"
                    id="reset-confirm-password"
                    inputProps={{
                      autoComplete: 'new-password',
                      autoCapitalize: 'none',
                      autoCorrect: 'off',
                      spellCheck: false,
                    }}
                  />
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;
